import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { TransactionFilters as FilterType } from '../types/commission';

interface TransactionFiltersProps {
  filters: FilterType;
  onChange: (filters: FilterType) => void;
}

/**
 * Filter panel for commission transactions
 * Allows filtering by date range and transaction type (commissions/draws)
 */
export function TransactionFilters({ filters, onChange }: TransactionFiltersProps) {
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, startDate: e.target.value || undefined });
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, endDate: e.target.value || undefined });
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onChange({
      ...filters,
      transactionType: value as FilterType['transactionType'],
    });
  };

  const handleThisMonth = () => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    onChange({
      ...filters,
      startDate: firstOfMonth.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
    });
  };

  const handleClear = () => {
    onChange({});
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[150px]">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={filters.startDate ?? ''}
              onChange={handleStartDateChange}
            />
          </div>

          <div className="flex-1 min-w-[150px]">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={filters.endDate ?? ''}
              onChange={handleEndDateChange}
            />
          </div>

          <div className="flex-1 min-w-[150px]">
            <Label htmlFor="transactionType">Type</Label>
            <Select
              id="transactionType"
              value={filters.transactionType ?? 'all'}
              onChange={handleTypeChange}
            >
              <option value="all">All Transactions</option>
              <option value="commissions">Commissions Only</option>
              <option value="draws">Draws Only</option>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleThisMonth}>
              This Month
            </Button>
            <Button variant="ghost" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
